// src/pages/Users/PassageCeintureDocuments/components/PassageCeintureDocumentCard.jsx

import React from "react";
import {
  FaEdit,
  FaEye,
  FaFilePdf,
  FaImage,
  FaTrash,
} from "react-icons/fa";

import {
  beltTheme,
  isQuillEmpty,
  toAbsoluteUrl,
  truncateText,
} from "../utils/passageCeintureDocumentsUtils";

export default function PassageCeintureDocumentCard({
  document,
  canManage,
  onOpen,
  onEdit,
  onDelete,
}) {
  const imageUrl = toAbsoluteUrl(document?.image_url);
  const pdfUrl = toAbsoluteUrl(document?.pdf_url);

  const hasDescription = !isQuillEmpty(document?.description);
  const descriptionPreview = truncateText(document?.description, 140);

  const theme = beltTheme(document?.ceinture);

  return (
    <article className="group overflow-hidden rounded-[1.65rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-950/10 dark:border-slate-800 dark:bg-slate-900">
      <button
        type="button"
        onClick={onOpen}
        className="relative block w-full overflow-hidden bg-slate-100 text-left dark:bg-slate-950"
        aria-label={`Ouvrir ${document?.titre || "document"}`}
      >
        <div className="aspect-[16/10] w-full">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={document?.titre || "Document"}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]"
              loading="lazy"
            />
          ) : (
            <div className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${theme.gradient}`}>
              <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-white/15 text-white backdrop-blur">
                {pdfUrl ? (
                  <FaFilePdf className="text-3xl" />
                ) : (
                  <FaImage className="text-3xl" />
                )}
              </div>
            </div>
          )}
        </div>

        <div className="absolute left-3 top-3">
          <span
            className={`inline-flex rounded-full px-3 py-1 text-[11px] font-bold uppercase ring-1 ${theme.badge}`}
          >
            {document?.ceinture || "Sans ceinture"}
          </span>
        </div>

        {!document?.is_published && (
          <div className="absolute right-3 top-3">
            <span className="inline-flex rounded-full bg-amber-500 px-3 py-1 text-[11px] font-bold uppercase text-white shadow-sm">
              Brouillon
            </span>
          </div>
        )}
      </button>

      <div className="p-4">
        <div className="min-h-[118px]">
          <h3 className="line-clamp-2 text-base font-bold leading-snug text-slate-950 dark:text-white">
            {document?.titre || "Document sans titre"}
          </h3>

          {hasDescription ? (
            <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {descriptionPreview}
            </p>
          ) : (
            <p className="mt-2 text-sm leading-6 text-slate-400">
              Aucune description disponible.
            </p>
          )}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onOpen}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-3 text-sm font-bold text-white transition active:scale-95 dark:bg-white dark:text-slate-950"
          >
            <FaEye />
            Voir
          </button>

          {pdfUrl ? (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-red-50 px-3 text-sm font-bold text-red-700 transition active:scale-95 dark:bg-red-950/30 dark:text-red-300"
            >
              <FaFilePdf />
              PDF
            </a>
          ) : (
            <span className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-3 text-sm font-bold text-slate-400 dark:bg-slate-800">
              <FaFilePdf />
              PDF
            </span>
          )}
        </div>

        {canManage && (
          <div className="mt-3 grid grid-cols-2 gap-2 border-t border-slate-200 pt-3 dark:border-slate-800">
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-3 text-xs font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
            >
              <FaEdit />
              Modifier
            </button>

            <button
              type="button"
              onClick={onDelete}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-red-50 px-3 text-xs font-bold text-red-700 transition active:scale-95 dark:bg-red-950/30 dark:text-red-300"
            >
              <FaTrash />
              Supprimer
            </button>
          </div>
        )}
      </div>
    </article>
  );
}